import React, { useCallback, useEffect, useState } from 'react';

import { t } from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from 'reactstrap';

import { MCToggle } from '../../../components/mc';

import {
  getNotificationsSettings,
  setNotificationsSettings,
} from '../../../store/notifications/actions';
import s from './Components.module.scss';

const NotificationsSetting = () => {
  const dispatch = useDispatch();
  const { notificationSettings, loading } = useSelector((state) => state.Notifications);
  const [settings, setSettings] = useState([]);
  const language = useSelector((state) => state.Layout.language);
  const [isSettingsChanged, setIsSettingsChanged] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(
    () => localStorage.getItem('mc_notif_sound') !== 'off',
  );

  const handleSoundToggle = () => {
    const next = !soundEnabled;
    if (next) {
      localStorage.removeItem('mc_notif_sound');
    } else {
      localStorage.setItem('mc_notif_sound', 'off');
    }
    setSoundEnabled(next);
  };

  const handleChange = ({ type, field, value }) => {
    setSettings((prevState) => {
      return prevState.map((item) => {
        if (item?.notification_type?.id === type?.id) {
          item[field] = value;
        }
        return item;
      });
    });
    setIsSettingsChanged(true);
  };

  const handleSubmit = useCallback(() => {
    dispatch(
      setNotificationsSettings(
        settings?.map((item) => ({
          site: item?.site,
          email: item?.email,
          notification_type_id: item?.notification_type?.id,
        })),
      ),
    );
    setIsSettingsChanged(false);
  }, [dispatch, settings]);

  useEffect(() => {
    dispatch(getNotificationsSettings(language));
  }, [dispatch, language]);

  useEffect(() => {
    setSettings(
      notificationSettings?.map((item) => ({
        notification_type: item?.notification_type,
        site: item?.notification_configuration?.site,
        email: item?.notification_configuration?.email,
      })),
    );
  }, [notificationSettings]);

  return (
    <div className={s.settingBlock}>
      <h4 className={s.blockTitle}>{t('user_profile_notification_setting')}</h4>

      <div className={s.notifyList}>
        {/* column legend — visible on wider layouts, replaced by inline labels on mobile */}
        <div className={s.notifyHead}>
          <span className={s.notifyColLabel}>{t('user_profile_notification_setting_site')}</span>
          <span className={s.notifyColLabel}>{t('user_profile_notification_setting_email')}</span>
        </div>

        {settings?.map((item) => (
          <div className={s.notifyRow} key={item?.notification_type?.id}>
            <div className={s.notifyName}>{item?.notification_type?.title}</div>
            <div className={s.notifyControls}>
              <div className={s.notifyControl}>
                <span className={s.notifyControlLabel}>
                  {t('user_profile_notification_setting_site')}
                </span>
                <MCToggle
                  checked={!!item?.site}
                  aria-label={`${item?.notification_type?.title} — ${t(
                    'user_profile_notification_setting_site',
                  )}`}
                  onChange={(value) =>
                    handleChange({ type: item?.notification_type, field: 'site', value })
                  }
                />
              </div>
              <div className={s.notifyControl}>
                <span className={s.notifyControlLabel}>
                  {t('user_profile_notification_setting_email')}
                </span>
                <MCToggle
                  checked={!!item?.email}
                  aria-label={`${item?.notification_type?.title} — ${t(
                    'user_profile_notification_setting_email',
                  )}`}
                  onChange={(value) =>
                    handleChange({ type: item?.notification_type, field: 'email', value })
                  }
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={s.notifyRow}>
        <div className={s.notifyName}>{t('notification_sound_label')}</div>
        <div className={s.notifyControls}>
          <div className={s.notifyControl}>
            <MCToggle
              checked={soundEnabled}
              aria-label={t('notification_sound_label')}
              onChange={handleSoundToggle}
            />
          </div>
        </div>
      </div>

      <button
        type="button"
        className={s.submitBtn}
        disabled={!isSettingsChanged}
        onClick={handleSubmit}>
        {loading ? (
          <div className="d-flex gap-2 justify-content-center align-items-center">
            <Spinner className="spinner" /> {t('common_save_changes')}
          </div>
        ) : (
          t('common_save_changes')
        )}
      </button>
    </div>
  );
};

export default NotificationsSetting;
